import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import './LopButton.css';

export type LopButtonVariant = 'primary' | 'secondary' | 'link' | 'danger';
export type LopButtonSize = 'sm' | 'md';

export interface LopButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  variant?: LopButtonVariant;
  size?: LopButtonSize;
  /** Leading icon (a lucide element). While loading it is swapped for the spinner. */
  icon?: React.ReactNode;
  /** Square icon-only button (toolbar). Needs aria-label / title. */
  iconOnly?: boolean;
  /** Externally tracked busy state (OR-ed with the automatic one). */
  loading?: boolean;
  /**
   * If the handler returns a promise the button tracks it itself: disabled +
   * spinner + aria-busy from the click frame until it settles (success or
   * failure), further clicks ignored (ref guard).
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => unknown;
}

export const LopButton = React.forwardRef<HTMLButtonElement, LopButtonProps>(function LopButton(
  {
    variant = 'secondary',
    size = 'md',
    icon,
    iconOnly = false,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  const guard = useRef(false);
  const mounted = useRef(true);
  const [pending, setPending] = useState(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const busy = loading || pending;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (guard.current || busy) {
        e.preventDefault();
        return;
      }
      const r = onClick?.(e);
      if (r && typeof (r as Promise<unknown>).then === 'function') {
        guard.current = true;
        setPending(true);
        void Promise.resolve(r).finally(() => {
          guard.current = false;
          if (mounted.current) setPending(false);
        });
      }
    },
    [onClick, busy],
  );

  const cls = [
    'lop-btn',
    `lop-btn--${variant}`,
    `lop-btn--${size}`,
    busy ? 'lop-btn--busy' : '',
    iconOnly ? 'lop-btn--icon' : '',
    icon ? '' : 'lop-btn--no-icon',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const spinner = <Loader2 size={size === 'sm' ? 12 : 14} className="lop-btn__spinner" aria-hidden="true" />;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      className={cls}
      disabled={disabled || busy}
      aria-busy={busy ? true : undefined}
      onClick={handleClick}
    >
      {icon ? (busy ? spinner : <span className="lop-btn__icon" aria-hidden="true">{icon}</span>) : null}
      {!iconOnly && children !== undefined && children !== null && children !== false && (
        <span className="lop-btn__label">{children}</span>
      )}
      {!icon && busy && <span className="lop-btn__overlay">{spinner}</span>}
    </button>
  );
});
